import { useEffect, useState } from 'react'
import { motion, useMotionValue, useSpring } from 'framer-motion'

export default function CustomCursor() {
  const cursorX = useMotionValue(-100)
  const cursorY = useMotionValue(-100)

  const springX = useSpring(cursorX, { stiffness: 800, damping: 50, mass: 0.2 })
  const springY = useSpring(cursorY, { stiffness: 800, damping: 50, mass: 0.2 })

  const [isHovering, setIsHovering] = useState(false)
  const [isClicking, setIsClicking] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  const isTouchDevice = typeof window !== 'undefined' && 
    (window.matchMedia('(hover: none)').matches || window.matchMedia('(pointer: coarse)').matches)

  const outerX = isTouchDevice ? cursorX : springX
  const outerY = isTouchDevice ? cursorY : springY

  useEffect(() => {
    setIsVisible(!isTouchDevice)

    if (isTouchDevice) {
      const handleTouchStart = (e) => {
        const touch = e.touches[0]
        cursorX.set(touch.clientX)
        cursorY.set(touch.clientY)
        setIsVisible(true)
      }

      const handleTouchMove = (e) => {
        const touch = e.touches[0]
        cursorX.set(touch.clientX)
        cursorY.set(touch.clientY)
      }

      const handleTouchEnd = () => {
        setIsVisible(false)
      }

      window.addEventListener('touchstart', handleTouchStart, { passive: true })
      window.addEventListener('touchmove', handleTouchMove, { passive: true })
      window.addEventListener('touchend', handleTouchEnd)
      window.addEventListener('touchcancel', handleTouchEnd)

      return () => {
        window.removeEventListener('touchstart', handleTouchStart)
        window.removeEventListener('touchmove', handleTouchMove)
        window.removeEventListener('touchend', handleTouchEnd)
        window.removeEventListener('touchcancel', handleTouchEnd)
      }
    }

    const move = (e) => {
      cursorX.set(e.clientX)
      cursorY.set(e.clientY)
    }
    window.addEventListener('mousemove', move)

    const handleEnter = () => setIsHovering(true)
    const handleLeave = () => setIsHovering(false)
    const handleDown = () => setIsClicking(true)
    const handleUp = () => setIsClicking(false)

    const addListeners = () => {
      const targets = document.querySelectorAll(
        'a, button, [role="button"], input, textarea, select, label, .cursor-hover'
      )
      targets.forEach(el => {
        el.addEventListener('mouseenter', handleEnter)
        el.addEventListener('mouseleave', handleLeave)
      })
    }

    addListeners()
    window.addEventListener('mousedown', handleDown)
    window.addEventListener('mouseup', handleUp)

    const observer = new MutationObserver(addListeners)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      window.removeEventListener('mousemove', move)
      const targets = document.querySelectorAll(
        'a, button, [role="button"], input, textarea, select, label, .cursor-hover'
      )
      targets.forEach(el => {
        el.removeEventListener('mouseenter', handleEnter)
        el.removeEventListener('mouseleave', handleLeave)
      })
      window.removeEventListener('mousedown', handleDown)
      window.removeEventListener('mouseup', handleUp)
      observer.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!isVisible) return null

  return (
    <>
      <motion.div
        animate={{
          width: isClicking ? '28px' : isHovering ? '64px' : '40px',
          height: isClicking ? '28px' : isHovering ? '64px' : '40px',
          borderRadius: '50%',
        }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          x: outerX,
          y: outerY,
          translateX: '-50%',
          translateY: '-50%',
          backgroundColor: 'white',
          mixBlendMode: 'difference',
          pointerEvents: 'none',
          zIndex: 999999,
        }}
      />

      <motion.div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          x: cursorX,
          y: cursorY,
          translateX: '-50%',
          translateY: '-50%',
          width: '5px',
          height: '5px',
          borderRadius: '50%',
          backgroundColor: 'white',
          mixBlendMode: 'difference',
          pointerEvents: 'none',
          zIndex: 999999,
        }}
      />
    </>
  )
}
